import { Box, Typography } from "@mui/material";
import {
  Apartment,
  CollectionsBookmark,
  Groups,
  HomeRounded,
  LoginRounded,
  Person,
  PersonAddAlt1Rounded,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ROUTES } from "@/shared/constants";
import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentOrganization } from "@/modules/organization/useCurrentOrganization";
import { isNavItemSelected } from "@/shared/utils/isNavItemSelected";
import { Settings } from "lucide-react";

type MobileFooterViewProps = {
  variant: "user" | "talent";
};

type MobileNavItem = {
  label: string;
  path: string;
  icon: JSX.Element;
};

/** Space reserved by LayoutMain so content clears this fixed bar. */
export const MOBILE_BOTTOM_NAV_OFFSET = "calc(80px + env(safe-area-inset-bottom, 0px))";

const MODAL_SELECTOR = '.MuiModal-root, [role="dialog"][aria-modal="true"], dialog[open]';

const hasOpenModal = () => {
  if (typeof document === "undefined") return false;

  return Array.from(document.querySelectorAll<HTMLElement>(MODAL_SELECTOR)).some((modal) => {
    const style = window.getComputedStyle(modal);
    return (
      modal.getAttribute("aria-hidden") !== "true" &&
      style.display !== "none" &&
      style.visibility !== "hidden"
    );
  });
};

const MobileFooterView = ({ variant }: MobileFooterViewProps) => {
  const { t } = useTranslation("sidebar");
  const location = useLocation();
  const { userInfo } = useAuth();
  const { isOrganizationWorkspace } = useCurrentOrganization();
  const isLoggedIn = Boolean(userInfo?.id);
  const [isModalOpen, setIsModalOpen] = useState(hasOpenModal);

  useEffect(() => {
    let animationFrame: number | undefined;

    const updateModalState = () => {
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => setIsModalOpen(hasOpenModal()));
    };

    const containsModal = (node: Node) =>
      node instanceof Element &&
      (node.matches(MODAL_SELECTOR) || node.querySelector(MODAL_SELECTOR) !== null);

    const observer = new MutationObserver((mutations) => {
      const modalChanged = mutations.some(
        (mutation) =>
          (mutation.type === "attributes" &&
            mutation.target instanceof Element &&
            mutation.target.matches(MODAL_SELECTOR)) ||
          (mutation.type === "childList" &&
            [...mutation.addedNodes, ...mutation.removedNodes].some(containsModal))
      );

      if (modalChanged) updateModalState();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-hidden", "aria-modal", "open"],
    });
    updateModalState();

    return () => {
      observer.disconnect();
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
    };
  }, []);

  const roleItems: MobileNavItem[] =
    variant === "talent"
      ? [
          { label: t("halls"), path: ROUTES.TALENTS, icon: <Groups /> },
          { label: t("charm"), path: ROUTES.CHARM, icon: <CollectionsBookmark /> },
          { label: t("profile"), path: ROUTES.TALENT_HALL, icon: <Person /> },
          { label: t("settings"), path: ROUTES.SETTINGS, icon: <Settings size={20} /> },

          // { label: t("inbox"), path: ROUTES.INBOX, icon: <MailOutlineRounded /> },
          // { label: t("wallet"), path: ROUTES.WALLET, icon: <Wallet /> },
        ]
      : isOrganizationWorkspace
        ? [
            { label: t("hall"), path: ROUTES.ORGANIZATION_HALL, icon: <Apartment /> },
            { label: t("charm"), path: ROUTES.CHARM, icon: <CollectionsBookmark /> },
            { label: t("settings"), path: ROUTES.SETTINGS, icon: <Settings size={20} /> },
          ]
        : [
          { label: t("halls"), path: ROUTES.TALENTS, icon: <Groups /> },
          { label: t("charm"), path: ROUTES.CHARM, icon: <CollectionsBookmark /> },
          { label: t("settings"), path: ROUTES.SETTINGS, icon: <Settings size={20} /> },
          // { label: t("inbox"), path: ROUTES.INBOX, icon: <MailOutlineRounded /> },
          // { label: t("wallet"), path: ROUTES.WALLET, icon: <Wallet /> },
        ];

  const homePath = isOrganizationWorkspace ? ROUTES.ORGANIZATION_LOBBY : ROUTES.LOBBY;
  const homeLabel = t("lobby");

  const navItems: MobileNavItem[] = isLoggedIn
    ? [
        { label: homeLabel, path: homePath, icon: <HomeRounded /> },
        ...roleItems,
      ]
    : [
        { label: t("lobby"), path: ROUTES.LOBBY, icon: <HomeRounded /> },
        { label: t("login"), path: ROUTES.SIGN_IN, icon: <LoginRounded /> },
        { label: t("sign_up"), path: ROUTES.REGISTER, icon: <PersonAddAlt1Rounded /> },
      ];
  const matchedIndex = navItems.findIndex((item) =>
    isNavItemSelected(location.pathname, item.path)
  );
  // Prefer a real match; only fall back to Lobby when the route is unrelated to any tab.
  const selectedIndex = matchedIndex >= 0 ? matchedIndex : 0;
  const notchLeft = `calc(${((selectedIndex + 0.5) / navItems.length) * 100}% - 39px)`;

  return (
    <Box
      component="nav"
      aria-label="Mobile navigation"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1301,
        height: "calc(100px + env(safe-area-inset-bottom))",
        pointerEvents: "none",
        opacity: isModalOpen ? 0 : 1,
        transform: isModalOpen ? "translateY(100%)" : "translateY(0)",
        transition: "transform 300ms cubic-bezier(.22,1,.36,1), opacity 200ms ease",
        willChange: "transform, opacity",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 27,
          bottom: 0,
          display: "flex",
          alignItems: "stretch",
          bgcolor: "var(--primary-100-light)",
          borderTop: "1px solid var(--primary-300)",
          borderRadius: "0 0 12px 12px",
          boxShadow: "var(--shadow-sm)",
          pointerEvents: "auto",
          pb: "env(safe-area-inset-bottom)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -1,
            left: notchLeft,
            width: 78,
            height: 40,
            background: "var(--primary-100-light)",
            borderBottomLeftRadius: 40,
            borderBottomRightRadius: 40,
            boxShadow: "0 6px 12px rgba(0,0,0,.12)",
            transition: "left .42s cubic-bezier(.22,1,.36,1)",
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {navItems.map((item, index) => {
            const selected = index === selectedIndex;

            return (
              <Box
                key={item.path}
                component={RouterLink}
                to={item.path}
                aria-current={selected ? "page" : undefined}
                sx={{
                  position: "relative",
                  minWidth: 0,
                  height: "100%",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  px: 0.25,
                  color: selected ? "var(--neutralWhite)" : "var(--primary-700)",
                  textDecoration: "none",
                  fontFamily: "var(--primary-font-family)",

                  WebkitTapHighlightColor: "transparent",
                  WebkitTouchCallout: "none",
                  userSelect: "none",
                  outline: "none",

                  "&:focus": {
                    outline: "none",
                  },

                  "&:focus-visible": {
                    outline: "none",
                  },

                  "&:active": {
                    backgroundColor: "transparent",
                  },

                  "& .MuiSvgIcon-root": {
                    width: 20,
                    height: 20,
                  },
                }}
              >
                {selected ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -20,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "var(--primary-700)",
                      border: "2px solid var(--primary-100-light)",
                      boxShadow: "var(--shadow-xs)",
                      "& .MuiSvgIcon-root": { width: 22, height: 22 },
                    }}
                  >
                    {item.icon}
                  </Box>
                ) : (
                  <>
                    {item.icon}
                    <Typography
                      component="span"
                      sx={{
                        maxWidth: "100%",
                        fontFamily: "var(--primary-font-family)",
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1,
                        color: "inherit",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default MobileFooterView;
